import { putOutboxItem, updateOutboxItem, deleteOutboxItem, getAllOutboxItems, getCachedData } from "./offlineDb"
import { upsertOutboxItem, removeOutboxItem } from "../redux/slices/offlineSlice"

/**
 * Phase 2 — outbox write path.
 *
 * `fetchOrQueue` wraps a network request used inside a Redux thunk. If the
 * request fails because the device is offline (not because the server
 * rejected it), the request is written to the IndexedDB outbox instead of
 * throwing, so the calling thunk can resolve optimistically and let the
 * auditor keep working. When connectivity returns, `syncManager.js` replays
 * the queued request.
 *
 * A genuine server-side error (4xx/5xx that isn't a network failure) still
 * throws normally — queuing is only for "we couldn't reach the server at
 * all", not for legitimate rejections.
 */

function isNetworkFailure(error) {
  if (typeof navigator !== "undefined" && navigator.onLine === false) return true
  // fetch() throws a TypeError ("Failed to fetch" / "NetworkError...") when
  // the browser cannot reach the network at all.
  return error?.name === "TypeError"
}

function generateId() {
  return `outbox_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
}

// Serialize writes for a single entity so simultaneous autosaves cannot
// both observe an empty outbox and create duplicate PUTs.
const updateWrites = new Map()
async function writeQueuedRequest(requestInit, meta, dispatch) {
  const canMerge = requestInit.method === "PUT" &&
    ["file-update", "work-update", "observation-update"].includes(meta.requestType)
  const key = canMerge ? JSON.stringify([meta.entityType, meta.entityId]) : null
  const previous = key && updateWrites.get(key)
  const operation = (async () => {
    if (previous) await previous.catch(() => {})
    const resolvedIds = canMerge ? await getCachedData("sync:resolved-ids") || {} : {}
    const canonical = (id) => resolvedIds[id] || id
    const matches = canMerge ? (await getAllOutboxItems()).filter((item) =>
      item.status !== "syncing" && item.method === "PUT" && item.meta?.requestType === meta.requestType &&
      item.meta?.entityType === meta.entityType && canonical(item.meta?.entityId) === meta.entityId) : []
    const item = await putOutboxItem({
      ...matches[0], id: matches[0]?.id || generateId(), ...requestInit,
      body: canMerge ? JSON.stringify(Object.assign({}, ...matches.map((item) => JSON.parse(item.body || "{}")),
        JSON.parse(requestInit.body || "{}"))) : requestInit.body,
      type: meta.requestType || undefined, meta, status: "pending", retryCount: 0,
      retryable: true, lastError: null, errorKind: null,
    })
    // Also collapse duplicates written by earlier application versions.
    for (const duplicate of matches.slice(1)) {
      await deleteOutboxItem(duplicate.id)
      if (dispatch) dispatch(removeOutboxItem(duplicate.id))
    }
    if (dispatch) dispatch(upsertOutboxItem(item))
    return item
  })()
  if (key) updateWrites.set(key, operation)
  try { return await operation } finally {
    if (key && updateWrites.get(key) === operation) updateWrites.delete(key)
  }
}

/**
 * @param {object} dispatch - Redux dispatch, used to keep offlineSlice in sync
 * @param {object} requestInit - { url, method, headers, body } describing the request to replay later
 * @param {object} meta - { description, entityType, entityId } for UI display and reconciliation
 * @param {Function} performRequest - () => Promise<Response|any> the actual fetch/service call to attempt now
 */
export async function fetchOrQueue({ dispatch, requestInit, meta = {}, performRequest, forceQueue = false }) {
  // If the device is already known to be offline, don't waste time
  // attempting a request that can only fail (and, on some connections, hang
  // for a while before it does) — go straight to the outbox. This also
  // guarantees an offline save can never slip through to the server, which
  // matters now that syncing back is a deliberate, manual action.
  const knownOffline = typeof navigator !== "undefined" && navigator.onLine === false

  if (!knownOffline && !forceQueue) {
    try {
      return { queued: false, result: await performRequest() }
    } catch (error) {
      if (!isNetworkFailure(error)) {
        throw error
      }
    }
  }

  const item = await writeQueuedRequest(requestInit, meta, dispatch)
  console.log("[v0] Network unavailable, queued request to outbox:", meta.description || requestInit.url)

  return { queued: true, outboxItem: item }
}

export async function loadOutboxIntoStore(dispatch) {
  const items = await getAllOutboxItems()
  return items
}

export async function markOutboxItemSynced(dispatch, id) {
  await deleteOutboxItem(id)
  if (dispatch) dispatch(removeOutboxItem(id))
}

export async function markOutboxItemFailed(dispatch, id, patch) {
  const updated = await updateOutboxItem(id, patch)
  if (dispatch && updated) dispatch(upsertOutboxItem(updated))
  return updated
}

/**
 * Deleting an entity that has queued (but not-yet-synced) outbox work
 * should cancel that work instead of letting it replay later — either
 * because the entity itself never reached the server (a pure offline
 * create, so a later "delete" request would have nothing to target) or
 * because any pending edits to it are now moot.
 *
 * Matches:
 *  - `file-upload` items by their own `clientId` (deleting that specific
 *    queued image) or by `metadata.entityType`/`entityId` (deleting the
 *    parent entity those files were attached to, e.g. an observation).
 *  - Any other queued request (`observation-create`, `observation-update`,
 *    `file-update`, `file-delete`, ...) by `meta.entityType` combined with
 *    `meta.entityId` or `meta.clientId`.
 *  - File edits/deletes by their saved parent reference, supplied file ids,
 *    or the aliases of a matching queued upload (including older entries).
 *
 * Returns the cancelled items so a caller can also clean up any local
 * optimistic state (e.g. placeholder file records) that referenced them.
 */
export async function cancelOutboxItemsForEntity(dispatch, { entityType, entityId, clientId, fileIds = [], childEntities = [] } = {}) {
  const items = await getAllOutboxItems()
  const entityIds = new Set([entityId, clientId].filter(Boolean))
  const relatedFileIds = new Set(fileIds.filter(Boolean))
  const descendants = [...childEntities]
  for (const item of items) {
    if (item.meta?.parentEntityType === entityType && entityIds.has(item.meta?.parentEntityId)) {
      descendants.push({ entityType: item.meta.entityType, entityId: item.meta.entityId })
    }
  }
  const belongsToEntity = (type, id) => (entityType && type === entityType && entityIds.has(id)) ||
    descendants.some((child) => child.entityType === type && child.entityId === id)

  // Collect upload aliases before filtering. File edits target pending_<id>,
  // not the parent work id, and must be cancelled with their upload.
  for (const item of items) {
    if (item.type === "file-upload" && (
      (clientId && item.clientId === clientId) ||
      belongsToEntity(item.metadata?.entityType, item.metadata?.entityId)
    )) {
      if (item.clientId) {
        relatedFileIds.add(item.clientId)
        relatedFileIds.add(`pending_${item.clientId}`)
      }
    }
  }
  if (entityType === "file" && entityId) relatedFileIds.add(entityId)
  if (clientId) {
    relatedFileIds.add(clientId)
    relatedFileIds.add(`pending_${clientId}`)
  }

  const matches = items.filter((item) => {
    if (item.type === "file-upload") {
      if (clientId && item.clientId === clientId) return true
      if (belongsToEntity(item.metadata?.entityType, item.metadata?.entityId)) return true
      return false
    }
    if (item.meta?.entityType === "file") {
      return belongsToEntity(item.meta.parentEntityType, item.meta.parentEntityId) ||
        relatedFileIds.has(item.meta.entityId) || relatedFileIds.has(item.meta.clientId)
    }
    if (belongsToEntity(item.meta?.parentEntityType, item.meta?.parentEntityId) || belongsToEntity(item.meta?.entityType, item.meta?.entityId)) return true
    if (entityType && item.meta?.entityType !== entityType) return false
    return (
      (entityId && (item.meta?.entityId === entityId || item.meta?.clientId === entityId)) ||
      (clientId && item.meta?.clientId === clientId)
    )
  })

  await Promise.all(matches.map((item) => deleteOutboxItem(item.id)))
  if (dispatch) matches.forEach((item) => dispatch(removeOutboxItem(item.id)))

  return matches
}
